import { Box } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { GroupChartDataSet } from "../../../api/dtos/dtos";
import CustomTooltip from "./CustomTooltip";

interface IRechartBarFormat {
    name: string; 
    [key: string]: number | string | null
}

interface IStackedBarChartProps {
    graphId: string | undefined,
    chartDataSet: GroupChartDataSet[] | undefined,
    chartLabels: string[] | undefined,
}

const StackedBarChart: FC<IStackedBarChartProps> = ({
    graphId,
    chartDataSet,
    chartLabels
}) => {
    //
    // -- States -- 
    const [formattedChartData, setFormattedChartData] = useState<IRechartBarFormat[] | undefined>(undefined);
    const [hiddenData, setHiddenData] = useState<Record<string, boolean>>({});

    // Format the chart data into recharts format
    useEffect(() => {
        // Generate dynamic X-axis labels
        const xAxisLabels = chartLabels; 

        // Format groupChartDataSets into Recharts-compatible data format
        const formattedData = xAxisLabels?.map((label, index) => {
            const entry: IRechartBarFormat = { name: label };

            // Add data to the formatted data set if dataset isn't hidden
            chartDataSet?.forEach((dataSet) => {
                entry[dataSet.dataSetLabel] = hiddenData[dataSet.dataSetLabel] ? null : dataSet.chartData[index];
            });

            return entry;
        });

        setFormattedChartData(formattedData);
    }, [chartDataSet, chartLabels, setFormattedChartData, hiddenData]);

    const handleOnClick = (o: any) => {
        const { value } = o;

        setHiddenData(current => ({
            ...current,
            [value]: !current[value]
        }));
    }

    return (
        (formattedChartData === undefined || formattedChartData?.length < 1) ? (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'start',
                    width: '100%',
                    height: '100%',
                    marginTop: '50px'
                }}
            >
                No data available to graph.
            </Box>
        ) : (
            <div
                id={graphId} 
                style={{ width: '100%', height: (((chartLabels?.length ?? 1) * 100) + 50) + 'px' }}
            >
                <ResponsiveContainer>
                    <BarChart
                        data={formattedChartData ?? []}
                        layout="vertical"
                        margin={{ 
                            top: 0, 
                            right: 30, 
                            left: 30, 
                            bottom: 0 
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                        onClick={handleOnClick}
                            formatter={(value, _entry, _index) => (
                                <span 
                                    style={{ 
                                        color: 'black',
                                        textDecoration: hiddenData[value] ? 'line-through' : 'none'
                                    }}
                                >
                                    {value}
                                </span>
                            )}
                        />
                        {chartDataSet?.map((dataset, index) => (
                            <Bar
                                key={`${dataset.dataSetLabel}-${index}`}
                                dataKey={dataset.dataSetLabel}
                                fill={dataset.backgroudColour}
                                stroke={dataset.borderColour}
                                stackId="a"
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        )
    );
    
}

export default StackedBarChart;